/* eslint-disable max-lines-per-function */
import { DYNAMISCHE_ABWESENHEIT_ARTEN } from '@/domain/mitarbeiter/features/anlageassistent/utils/constants/dynamischeAbwesenheitstypen';
import {
  Feiertagsausgleich,
  FeiertagsDynamik,
} from '@/domain/mitarbeiter/features/anlageassistent/utils/constants/feiertagsausgleichRegelung';
import {
  Arbeitstage,
  arbeitstage,
} from '@/domain/mitarbeiter/features/anlageassistent/utils/constants/tage';
import { useTranslation } from 'react-i18next';
import z from 'zod';

export function useAbwesenheitenSchema() {
  const { t } = useTranslation();
  const AbwesenheitenAllgemeinSchema = z
    .object({
      anzahlUrlaubstage: z
        .string()
        .min(1, {
          error: t(
            'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.anzahlUrlaubstage.validationError',
          ),
        })
        .refine(
          (val) => {
            const rest = Math.abs(Number(val.replace(',', '.')) % 1);
            return rest === 0 || rest === 0.5;
          },
          {
            error: t(
              'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.anzahlUrlaubstage.validationErrorFormat',
            ),
          },
        ),
      anzahlArbeitstage: z
        .custom<keyof Arbeitstage>()
        .nullable()
        .refine((val) => val && Object.keys(arbeitstage).includes(val), {
          message: t(
            'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.arbeitstage.validationError',
          ),
        }),
      stundenBewertung: z
        .enum(['fest', 'dynamisch'])
        .nullable()
        .refine((val) => !!val, {
          message: t(
            'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.stundenbewertung.validationError',
          ),
        }),
      stunden: z.string().nullable(),
      dynamisch: z.enum(Object.values(DYNAMISCHE_ABWESENHEIT_ARTEN)).nullable(),
      dynamisch_wochenschnitt_start: z.string().nullable(),
      dynamisch_wochenschnitt_limitieren: z.boolean().nullable(),
      dynamisch_drei_monatsschnitt_berechnungsgrundlage: z
        .enum(['moegliche_arbeitstage', 'anzahl_sv_tage'])
        .nullable(),
    })
    // eslint-disable-next-line complexity
    .superRefine((data, ctx) => {
      if (data.stundenBewertung === 'fest' && !data.stunden) {
        ctx.addIssue({
          code: 'custom',
          message: t(
            'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.stunden.validationError',
          ),
          path: ['stunden'],
        });
      } else if (data.stundenBewertung === 'dynamisch') {
        if (!data.dynamisch) {
          ctx.addIssue({
            code: 'custom',
            message: t(
              'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.dynamisch.validationError',
            ),
            path: ['dynamisch'],
          });
        } else {
          switch (data.dynamisch) {
            case 'wochenschnitt':
              if (!data.dynamisch_wochenschnitt_start) {
                ctx.addIssue({
                  code: 'custom',
                  message: t(
                    'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.dynamisch.wochendurchschnitt.start_abwesenheitszeit.validationError',
                  ),
                  path: ['dynamisch_wochenschnitt_start'],
                });
              }
              break;

            case 'drei_monatsschnitt':
              if (!data.dynamisch_drei_monatsschnitt_berechnungsgrundlage) {
                ctx.addIssue({
                  code: 'custom',
                  message: t(
                    'domain.mitarbeiter.create.steps.abwesenheiten.allgemein.form.dynamisch.drei_monatsdurchschnitt.berechnungsgrundlage.validationError',
                  ),
                  path: ['dynamisch_drei_monatsschnitt_berechnungsgrundlage'],
                });
              }
              break;

            default:
              break;
          }
        }
      }
    });

  const FeiertagseinstellungenSchema = z.object({
    feiertagsdynamik: z.custom<FeiertagsDynamik>().nullable(),
    feiertagsausgleich: z.custom<Feiertagsausgleich>().nullable(),
    /**
     * Dieses Feld wurde nur hinzugefügt um die TypeInferenz ohne strict mode zu gewährleisten, da die restlichen Felder alle optional sind. Es sollte nicht in der UI verwendet werden.
     * TODO: Entfernen sobald strict mode aktiv ist
     * */
    strict: z.boolean(),
  });

  const GuttageSchema = z.object({
    guttageBewerten: z.boolean(),
  });

  return {
    AbwesenheitenAllgemeinSchema,
    FeiertagseinstellungenSchema,
    GuttageSchema,
  };
}
